import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { TrophyIcon, PhotoIcon, NewspaperIcon, ArrowLeftIcon, MagnifyingGlassPlusIcon, MagnifyingGlassMinusIcon, ArrowsPointingOutIcon, ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon, PlayIcon, PauseIcon, XMarkIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import type { PhotoCategory, Photo } from '../../types/content';

interface DbPressArticle {
  id: string;
  tournament_id: string | null;
  source: string;
  title: string;
  date: string | null;
  quote: string;
  link: string;
  image_url: string | null;
  target?: string;
}

interface GalleryAlbum {
  name: string;
  photos: Photo[];
  coverUrl: string;
}

const ZOOM_LEVELS = [1, 1.5, 2, 3];
const SPEED_OPTIONS = [
  { label: '0.5×', ms: 6000 },
  { label: '1×', ms: 3000 },
  { label: '1.5×', ms: 2000 },
  { label: '2×', ms: 1500 },
  { label: '3×', ms: 1000 },
];

interface TournamentChampion {
  id: string;
  tournament_id: string;
  division: 'male' | 'female';
  player_name: string;
  player_country: string;
  score: string;
  player_image: string | null;
  highlights: string[];
}

interface TournamentWithChampions {
  id: string;
  name: string;
  start_date: string;
  year: number;
  champions: {
    male?: TournamentChampion;
    female?: TournamentChampion;
  };
}

interface GalleryItem {
  id: string;
  tournament_id: string | null;
  categories: PhotoCategory[];
}

type Tab = 'champions' | 'gallery' | 'press';

export default function MobileHistory() {
  const location = useLocation();
  const locationState = location.state as { tab?: Tab; tournamentId?: string } | null;
  const [activeTab, setActiveTab] = useState<Tab>(locationState?.tab ?? 'champions');
  const [allTournaments, setAllTournaments] = useState<TournamentWithChampions[]>([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState<string | null>(locationState?.tournamentId ?? null);
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
  const [pressArticles, setPressArticles] = useState<DbPressArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageTitle, setPageTitle] = useState('Tournament History');

  const [championPhotoUrl, setChampionPhotoUrl] = useState<string | null>(null);
  const [selectedAlbum, setSelectedAlbum] = useState<GalleryAlbum | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPlaying, setIsPlaying] = useState(false);
  const [speedIndex, setSpeedIndex] = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const panStart = useRef({ x: 0, y: 0 });
  const touchStartX = useRef(0);
  const selectorRef = useRef<HTMLDivElement>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [leaderboardPublished, setLeaderboardPublished] = useState(true);
  const [tournamentHasLeaderboard, setTournamentHasLeaderboard] = useState(false);

  useEffect(() => {
    Promise.all([fetchTournamentChampions(), fetchContent(), fetchLeaderboardStatus()]).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (selectedTournamentId) fetchPressArticles(selectedTournamentId);
  }, [selectedTournamentId]);

  useEffect(() => {
    if (selectedTournamentId) checkTournamentLeaderboard(selectedTournamentId);
    else setTournamentHasLeaderboard(false);
  }, [selectedTournamentId]);

  const fetchLeaderboardStatus = async () => {
    try {
      const { data } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'leaderboard_published')
        .maybeSingle();
      if (data) setLeaderboardPublished(data.value === true || data.value === 'true');
    } catch {}
  };

  const checkTournamentLeaderboard = async (tid: string) => {
    try {
      const { count } = await supabase
        .from('leaderboard_view')
        .select('id', { count: 'exact', head: true })
        .eq('tournament_id', tid);
      setTournamentHasLeaderboard((count ?? 0) > 0);
    } catch {
      setTournamentHasLeaderboard(false);
    }
  };

  const fetchTournamentChampions = async () => {
    try {
      const { data: tournamentsData } = await supabase
        .from('tournaments')
        .select('*')
        .order('start_date', { ascending: false });

      if (!tournamentsData?.length) return;

      const { data: championsData } = await supabase
        .from('tournament_champions')
        .select('*')
        .in('tournament_id', tournamentsData.map(t => t.id));

      const result: TournamentWithChampions[] = tournamentsData
        .map(t => {
          const tChamps = championsData?.filter(c => c.tournament_id === t.id) || [];
          return {
            id: t.id,
            name: t.name,
            start_date: t.start_date,
            year: new Date(t.start_date).getFullYear(),
            champions: {
              male: tChamps.find(c => c.division === 'male'),
              female: tChamps.find(c => c.division === 'female'),
            },
          };
        })
      setAllTournaments(result);
      if (result.length > 0) {
        setSelectedTournamentId(prev => {
          if (prev && result.some(t => t.id === prev)) return prev;
          const withContent = result.filter(t => t.champions.male || t.champions.female);
          return withContent.length > 0 ? withContent[0].id : result[0].id;
        });
      }
    } catch {}
  };

  const fetchPressArticles = async (tournamentId: string) => {
    try {
      const { data, error } = await supabase
        .from('press_articles')
        .select('*')
        .eq('tournament_id', tournamentId)
        .order('date', { ascending: false, nullsFirst: false });
      if (error) throw error;
      setPressArticles(data || []);
    } catch {}
  };

  const fetchContent = async () => {
    try {
      const { data: sections } = await supabase
        .from('content_sections')
        .select('id, title, content_items(*)')
        .eq('page', 'history');

      if (!sections) return;

      const headerSection = sections.find(s => s.title === 'Page Header');
      const gallerySection = sections.find(s => s.title === 'Gallery');

      const parse = (item: any) => {
        if (!item?.content) return null;
        let content = item.content;
        let attempts = 0;
        while (typeof content === 'string' && attempts < 5) {
          try { content = JSON.parse(content); attempts++; } catch { break; }
        }
        return content;
      };

      const titleItem = headerSection?.content_items?.find((i: any) => i.title === 'Title' && i.status === 'Published');
      if (titleItem) setPageTitle(parse(titleItem) || 'Tournament History');

      const rawGallery = gallerySection?.content_items?.filter((i: any) => i.status === 'Published' && i.type === 'gallery') || [];
      const items: GalleryItem[] = [];
      for (const item of rawGallery) {
        const parsed = parse(item);
        if (Array.isArray(parsed)) {
          items.push({ id: item.id, tournament_id: item.tournament_id ?? null, categories: parsed });
        }
      }
      setGalleryItems(items);

    } catch {}
  };

  const tournamentsWithContent = allTournaments.filter(t => {
    const hasChampions = t.champions.male || t.champions.female;
    const hasGallery = galleryItems.some(g => g.tournament_id === t.id);
    return hasChampions || hasGallery;
  });

  const selectedTournament = allTournaments.find(t => t.id === selectedTournamentId) ?? null;

  const filteredGalleryAlbums: GalleryAlbum[] = galleryItems
    .filter(g => !selectedTournamentId || g.tournament_id === selectedTournamentId || g.tournament_id === null)
    .flatMap(g => g.categories)
    .map(cat => {
      const validPhotos = (cat.photos || []).filter((p: Photo) => p?.url?.trim());
      return validPhotos.length > 0 ? { name: cat.name, photos: validPhotos, coverUrl: validPhotos[0].url } : null;
    })
    .filter(Boolean) as GalleryAlbum[];

  const handleTournamentSelect = (id: string) => {
    setSelectedTournamentId(id);
    setSelectedAlbum(null);
    closeLightbox();
  };

  const clearSlideshow = useCallback(() => {
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
  }, []);

  const startSlideshow = useCallback((ms: number, count: number) => {
    clearSlideshow();
    intervalRef.current = setInterval(() => {
      setLightboxIndex(prev => prev === null ? 0 : (prev === count - 1 ? 0 : prev + 1));
    }, ms);
  }, [clearSlideshow]);

  useEffect(() => {
    const count = selectedAlbum?.photos.length || 0;
    if (isPlaying && lightboxIndex !== null && count > 1) {
      startSlideshow(SPEED_OPTIONS[speedIndex].ms, count);
    } else {
      clearSlideshow();
    }
    return clearSlideshow;
  }, [isPlaying, lightboxIndex, speedIndex, selectedAlbum?.photos.length]);

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
    setIsPlaying(false);
  };

  const closeLightbox = () => {
    setLightboxIndex(null);
    setIsPlaying(false);
    clearSlideshow();
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  };

  const prevPhoto = useCallback(() => {
    if (lightboxIndex === null || !selectedAlbum) return;
    setLightboxIndex((lightboxIndex - 1 + selectedAlbum.photos.length) % selectedAlbum.photos.length);
    setZoomLevel(1); setPanOffset({ x: 0, y: 0 });
  }, [lightboxIndex, selectedAlbum]);

  const nextPhoto = useCallback(() => {
    if (lightboxIndex === null || !selectedAlbum) return;
    setLightboxIndex((lightboxIndex + 1) % selectedAlbum.photos.length);
    setZoomLevel(1); setPanOffset({ x: 0, y: 0 });
  }, [lightboxIndex, selectedAlbum]);

  const zoomIn = useCallback(() => {
    setZoomLevel(prev => {
      const idx = ZOOM_LEVELS.indexOf(prev);
      return idx < ZOOM_LEVELS.length - 1 ? ZOOM_LEVELS[idx + 1] : prev;
    });
  }, []);

  const zoomOut = useCallback(() => {
    setZoomLevel(prev => {
      const idx = ZOOM_LEVELS.indexOf(prev);
      if (idx <= 0) return 1;
      const next = ZOOM_LEVELS[idx - 1];
      if (next === 1) setPanOffset({ x: 0, y: 0 });
      return next;
    });
  }, []);

  const resetZoom = useCallback(() => { setZoomLevel(1); setPanOffset({ x: 0, y: 0 }); }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoomLevel <= 1) return;
    isDragging.current = true;
    dragStart.current = { x: e.clientX, y: e.clientY };
    panStart.current = { ...panOffset };
    e.preventDefault();
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current) return;
    setPanOffset({ x: panStart.current.x + e.clientX - dragStart.current.x, y: panStart.current.y + e.clientY - dragStart.current.y });
  };
  const handleMouseUp = () => { isDragging.current = false; };

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchStartX.current = touch.clientX;
    if (zoomLevel <= 1) return;
    isDragging.current = true;
    dragStart.current = { x: touch.clientX, y: touch.clientY };
    panStart.current = { ...panOffset };
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current) return;
    const touch = e.touches[0];
    setPanOffset({ x: panStart.current.x + touch.clientX - dragStart.current.x, y: panStart.current.y + touch.clientY - dragStart.current.y });
    e.stopPropagation();
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (isDragging.current) { isDragging.current = false; return; }
    if (zoomLevel > 1) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) {
      if (dx < 0) nextPhoto(); else prevPhoto();
      if (isPlaying) setIsPlaying(false);
    }
  };

  const handleSpeedChange = (idx: number) => {
    setSpeedIndex(idx);
    setShowSpeedMenu(false);
    if (isPlaying && selectedAlbum) startSlideshow(SPEED_OPTIONS[idx].ms, selectedAlbum.photos.length);
  };

  const canZoomIn = ZOOM_LEVELS.indexOf(zoomLevel) < ZOOM_LEVELS.length - 1;
  const canZoomOut = zoomLevel > 1;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-golf-orange border-t-transparent" />
      </div>
    );
  }

  const tabs: { id: Tab; label: string; icon: React.ComponentType<React.SVGProps<SVGSVGElement>> }[] = [
    { id: 'champions', label: 'Champions', icon: TrophyIcon },
    { id: 'gallery', label: 'Gallery', icon: PhotoIcon },
    { id: 'press', label: 'Press', icon: NewspaperIcon },
  ];

  const championsForSelected = selectedTournament
    ? [selectedTournament.champions.male, selectedTournament.champions.female].filter(Boolean) as TournamentChampion[]
    : [];

  return (
    <div className="pb-10">
      {/* Header */}
      <div
        className="relative h-32 bg-cover bg-center"
        style={{ backgroundImage: `url(https://images.pexels.com/photos/1325681/pexels-photo-1325681.jpeg?auto=compress&w=800)` }}
      >
        <div className="absolute inset-0 bg-golf-gray/75" />
        <div className="relative z-20 h-full flex items-end justify-between p-4">
          <div>
            <p className="text-golf-orange text-xs font-semibold uppercase tracking-wider mb-1">Ibom Open</p>
            <h1 className="text-white font-display font-bold text-xl">{pageTitle}</h1>
          </div>

          {tournamentsWithContent.length > 0 && (
            <div className="relative mb-1" ref={selectorRef}>
              <button
                onClick={() => setShowDropdown(v => !v)}
                className="flex items-center gap-1.5 bg-white/15 backdrop-blur-sm border border-white/25 rounded-xl px-3 py-2 text-white text-xs font-semibold"
              >
                <span className="max-w-[110px] truncate">
                  {selectedTournament ? selectedTournament.name : 'Select'}
                </span>
                <ChevronDownIcon className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
              </button>

              {showDropdown && (
                <div className="absolute right-0 top-full mt-1.5 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden min-w-[160px] z-50">
                  {tournamentsWithContent.map(t => (
                    <button
                      key={t.id}
                      onClick={() => { handleTournamentSelect(t.id); setShowDropdown(false); }}
                      className={`w-full text-left px-4 py-3 text-xs font-semibold transition-colors ${
                        selectedTournamentId === t.id
                          ? 'text-golf-orange bg-orange-50'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {t.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-100 bg-white sticky top-14 z-10">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-semibold transition-colors border-b-2 ${
              activeTab === tab.id
                ? 'text-golf-orange border-golf-orange'
                : 'text-gray-400 border-transparent'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {championPhotoUrl && (
        <div className="fixed inset-0 z-[200] bg-black flex flex-col" onClick={() => setChampionPhotoUrl(null)}>
          <button className="absolute top-4 right-4 z-10 p-2 bg-black/50 backdrop-blur-sm rounded-full" onClick={() => setChampionPhotoUrl(null)}>
            <XMarkIcon className="w-6 h-6 text-white" />
          </button>
          <img
            src={championPhotoUrl}
            alt="Champion"
            className="absolute inset-0 w-full h-full object-contain"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}

      {/* Champions Tab */}
      {activeTab === 'champions' && (
        <div className="px-4 pt-4">
          {!selectedTournament || championsForSelected.length === 0 ? (
            <div className="text-center py-16">
              <TrophyIcon className="w-12 h-12 mx-auto mb-3 text-gray-200" />
              <p className="text-gray-400 text-sm">No champions data available yet.</p>
            </div>
          ) : (
            <div>
<div className="flex flex-col gap-3">
                {championsForSelected.map((champion, i) => (
                  <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="relative h-40">
                      <img
                        src={champion.player_image || 'https://images.pexels.com/photos/914682/pexels-photo-914682.jpeg?auto=compress&w=400'}
                        alt={champion.player_name}
                        className="w-full h-full object-cover cursor-pointer"
                        loading="lazy"
                        onClick={() => setChampionPhotoUrl(champion.player_image || 'https://images.pexels.com/photos/914682/pexels-photo-914682.jpeg?auto=compress&w=400')}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent pointer-events-none" />
                      <div className="absolute bottom-0 left-0 p-3 w-full">
                        <p className="text-golf-orange text-[10px] font-semibold uppercase tracking-wide mb-0.5">
                          {champion.division === 'male' ? 'Male' : 'Female'} Division Champion
                        </p>
                        <h3 className="text-white font-display font-bold text-base leading-tight">
                          {champion.player_name}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-white/80 text-xs">{champion.player_country}</span>
                          {champion.score && (
                            <>
                              <span className="w-1 h-1 rounded-full bg-golf-orange" />
                              <span className="text-golf-orange text-xs font-bold">{champion.score}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    {champion.highlights?.length > 0 && (
                      <div className="p-3">
                        <ul className="space-y-1.5">
                          {champion.highlights.map((h, hi) => (
                            <li key={hi} className="flex items-start gap-2 text-xs text-gray-500">
                              <span className="w-1.5 h-1.5 rounded-full bg-golf-orange mt-1 flex-shrink-0" />
                              {h}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {leaderboardPublished && selectedTournament && tournamentHasLeaderboard && (
                <Link
                  to={`/m/leaderboard?tournamentId=${selectedTournament.id}`}
                  className="flex items-center justify-between w-full bg-golf-orange/10 border border-golf-orange/20 rounded-2xl px-4 py-3 mt-4"
                >
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-xl bg-golf-orange/15 flex items-center justify-center">
                      <ChartBarIcon className="w-4 h-4 text-golf-orange" />
                    </div>
                    <span className="text-sm font-semibold text-golf-gray">View Leaderboard</span>
                  </div>
                  <ChevronRightIcon className="w-4 h-4 text-golf-orange" />
                </Link>
              )}
            </div>
          )}
        </div>
      )}

      {/* Gallery Tab */}
      {activeTab === 'gallery' && !selectedAlbum && (
        <div className="px-4 pt-4">
          {filteredGalleryAlbums.length === 0 ? (
            <div className="text-center py-16">
              <PhotoIcon className="w-12 h-12 mx-auto mb-3 text-gray-200" />
              <p className="text-gray-400 text-sm">No gallery photos available yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 pb-8">
              {filteredGalleryAlbums.map((album, i) => (
                <div
                  key={i}
                  className="text-left rounded-2xl overflow-hidden bg-white border border-gray-100 shadow-sm"
                >
                  <button
                    onClick={() => setSelectedAlbum(album)}
                    className="w-full aspect-[4/3] bg-gray-100 overflow-hidden block active:opacity-80 transition-opacity"
                  >
                    <img
                      src={album.coverUrl}
                      alt={album.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </button>
                  <button
                    onClick={() => setSelectedAlbum(album)}
                    className="w-full p-3 text-left active:bg-gray-50 transition-colors"
                  >
                    <p className="font-semibold text-golf-gray text-sm truncate">{album.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{album.photos.length} photo{album.photos.length !== 1 ? 's' : ''}</p>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Album detail view */}
      {activeTab === 'gallery' && selectedAlbum && (
        <div className="pb-8">
          <div className="sticky top-14 z-20 bg-white border-b border-gray-100 px-4 py-3 flex items-center gap-3">
            <button
              onClick={() => { setSelectedAlbum(null); closeLightbox(); }}
              className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5 text-gray-700" />
            </button>
            <div className="flex-1 min-w-0">
              <h2 className="font-display font-bold text-golf-gray text-base truncate">{selectedAlbum.name}</h2>
              <p className="text-xs text-gray-400">{selectedAlbum.photos.length} photos</p>
            </div>
          </div>

          <div className="px-4 pt-4 grid grid-cols-3 gap-1.5">
            {selectedAlbum.photos.map((photo, i) => (
              <button
                key={i}
                onClick={() => openLightbox(i)}
                className="aspect-square rounded-lg overflow-hidden bg-gray-100"
              >
                <img
                  src={photo.url}
                  alt={photo.caption || ''}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </button>
            ))}
          </div>

          {lightboxIndex !== null && (
            <div className="fixed inset-0 z-[100] bg-black flex flex-col">
              <div className="absolute top-0 inset-x-0 z-10 flex items-center justify-between px-3 pt-safe py-3 bg-gradient-to-b from-black/70 to-transparent">
                <div className="flex items-center gap-1">
                  <div className="flex items-center gap-0.5 bg-black/50 backdrop-blur-sm rounded-full px-1.5 py-1">
                    <button onClick={zoomOut} disabled={!canZoomOut} className="p-1.5 text-white disabled:opacity-30 active:text-golf-orange transition-colors">
                      <MagnifyingGlassMinusIcon className="w-5 h-5" />
                    </button>
                    <button onClick={resetZoom} className="px-1.5 text-white text-xs font-medium active:text-golf-orange min-w-[2.5rem] text-center">
                      {Math.round(zoomLevel * 100)}%
                    </button>
                    <button onClick={zoomIn} disabled={!canZoomIn} className="p-1.5 text-white disabled:opacity-30 active:text-golf-orange transition-colors">
                      <MagnifyingGlassPlusIcon className="w-5 h-5" />
                    </button>
                  </div>
                  {selectedAlbum.photos.length > 1 && (
                    <div className="flex items-center gap-0.5 bg-black/50 backdrop-blur-sm rounded-full px-1.5 py-1">
                      <button onClick={() => setIsPlaying(p => !p)} className="p-1.5 text-white active:text-golf-orange transition-colors">
                        {isPlaying ? <PauseIcon className="w-5 h-5" /> : <PlayIcon className="w-5 h-5" />}
                      </button>
                      <div className="relative">
                        <button onClick={() => setShowSpeedMenu(s => !s)} className="px-1.5 text-white text-xs font-medium active:text-golf-orange min-w-[2.2rem] text-center">
                          {SPEED_OPTIONS[speedIndex].label}
                        </button>
                        {showSpeedMenu && (
                          <div className="absolute top-full left-0 mt-1 bg-black/90 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden min-w-[4.5rem] shadow-xl z-50">
                            {SPEED_OPTIONS.map((opt, i) => (
                              <button key={opt.label} onClick={() => handleSpeedChange(i)} className={`w-full px-3 py-2.5 text-xs text-left transition-colors ${i === speedIndex ? 'bg-golf-orange/20 text-golf-orange font-semibold' : 'text-white'}`}>
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-white text-sm font-medium bg-black/50 backdrop-blur-sm rounded-full px-3 py-1">
                    {lightboxIndex + 1} / {selectedAlbum.photos.length}
                  </span>
                  <button className="p-2 bg-black/50 backdrop-blur-sm rounded-full" onClick={closeLightbox}>
                    <XMarkIcon className="w-5 h-5 text-white" />
                  </button>
                </div>
              </div>

              {zoomLevel > 1 && (
                <div className="absolute top-14 left-3 z-20">
                  <button onClick={resetZoom} className="flex items-center gap-1 bg-black/60 backdrop-blur-sm rounded-full px-3 py-1.5 text-white text-xs">
                    <ArrowsPointingOutIcon className="w-4 h-4" />
                    Reset zoom
                  </button>
                </div>
              )}

              <div
                className="flex-1 min-h-0 relative overflow-hidden"
                style={{ cursor: zoomLevel > 1 ? 'grab' : 'default' }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                {selectedAlbum.photos.length > 1 && zoomLevel === 1 && (
                  <>
                    <button className="absolute left-2 top-1/2 -translate-y-1/2 z-10 p-2 bg-white/10 rounded-full" onClick={e => { e.stopPropagation(); prevPhoto(); if (isPlaying) setIsPlaying(false); }}>
                      <ChevronLeftIcon className="w-6 h-6 text-white" />
                    </button>
                    <button className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-2 bg-white/10 rounded-full" onClick={e => { e.stopPropagation(); nextPhoto(); if (isPlaying) setIsPlaying(false); }}>
                      <ChevronRightIcon className="w-6 h-6 text-white" />
                    </button>
                  </>
                )}
                <img
                  src={selectedAlbum.photos[lightboxIndex].url}
                  alt={selectedAlbum.photos[lightboxIndex].caption || ''}
                  className="absolute inset-0 w-full h-full object-contain select-none"
                  style={{ transform: `scale(${zoomLevel}) translate(${panOffset.x / zoomLevel}px, ${panOffset.y / zoomLevel}px)`, transformOrigin: 'center center', transition: 'transform 0.2s' }}
                  draggable={false}
                  onDoubleClick={() => zoomLevel === 1 ? zoomIn() : resetZoom()}
                />
              </div>

              {selectedAlbum.photos[lightboxIndex].caption && (
                <div className="px-4 pb-safe text-center bg-gradient-to-t from-black/60 to-transparent pt-4">
                  <p className="text-white text-sm">{selectedAlbum.photos[lightboxIndex].caption}</p>
                </div>
              )}
              <div className="pb-safe px-4 py-2 flex items-center justify-center gap-4 text-white/25 text-[10px]">
                <span>Swipe to navigate</span>
                <span>Double-tap to zoom</span>
                <span>Drag when zoomed</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Press Tab */}
      {activeTab === 'press' && (
        <div className="px-4 pt-4">
          {pressArticles.length === 0 ? (
            <div className="text-center py-16">
              <NewspaperIcon className="w-12 h-12 mx-auto mb-3 text-gray-200" />
              <p className="text-gray-400 text-sm">No press articles available yet.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {pressArticles.map((article) => (
                <div key={article.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-golf-orange text-xs font-semibold">{article.source}</span>
                    <span className="text-gray-400 text-xs">
                      {article.date ? new Date(article.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : ''}
                    </span>
                  </div>
                  <h3 className="font-semibold text-golf-gray text-sm leading-snug mb-2">{article.title}</h3>
                  {article.quote && (
                    <div className="flex gap-2 mb-3">
                      <div className="w-0.5 bg-golf-orange rounded-full flex-shrink-0" />
                      <blockquote className="text-xs text-gray-500 italic leading-relaxed">"{article.quote}"</blockquote>
                    </div>
                  )}
                  {article.link && (
                    <a
                      href={article.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-golf-orange font-semibold"
                    >
                      Read Full Article →
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

    </div>
  );
}
